import React, { useEffect, useMemo } from 'react'
import LeHotelMultiCartReviewTile from './LeHotelMultiCartReviewTile'
import Group from 'components/utils/Group'
import styled from 'styled-components'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Pane from 'components/Common/Pane'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Sheet from 'components/utils/Sheet'
import Divider from 'LuxKit/Divider'
import zIndex from 'styles/tools/z-index'
import { rem } from 'polished'
import LineTrashAltIcon from 'LuxKit/Icons/line/LineTrashAltIcon'
import noop from 'lib/function/noop'
import { dateIsBeforeOrSame, formatDynamicDateRange } from 'lib/datetime/dateUtils'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LoadingMultiRoomGroupHeaderSkeleton from 'components/Pages/CartReviewPage/LoadingMultiRoomGroupHeaderSkeleton'
import LoadingMultiRoomGroupButtonsSkeleton from 'components/Pages/CartReviewPage/LoadingMultiRoomGroupButtonsSkeleton'
import IconButton from 'LuxKit/Button/IconButton'
import cn from 'clsx'
import offerPageURL from 'lib/offer/offerPageURL'
import { queryKeyAdults, queryKeyCheckIn, queryKeyCheckOut, queryKeyChildren } from 'constants/url'
import { encodeChildrenOccupancy } from 'lib/search/searchUtils'
import useOffer from 'hooks/Offers/useOffer'
import NestableClickable from 'components/Common/NestableClickable'
import { MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { useSelector } from '@xstate/react'
import { isAccommodationItem, isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import MultiCartReviewTileOverlay from './_MultiCartReviewTileOverlay'
import { MultiCartReviewMachineContext } from '../MultiCartReviewMachineContext'
import SharedCartComments from '../Comments/SharedCartComments'
import { useAppSelector } from 'hooks/reduxHooks'
import { isLEAdmin } from 'selectors/accountSelectors'

const MultiRoomHotelMultiCartReviewGroupContainer = styled(Pane)`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border: none;
  ${mediaQueryUp.tablet} {
    border: 1px solid ${props => props.theme.palette.neutral.default.five};
  }
  &.is-condensed {
    border: none;
  }
`

const StyledLeHotelMultiCartReviewTile = styled(LeHotelMultiCartReviewTile)`
  border: none;
`

const MultiRoomHotelMultiCartReviewGroupContent = styled(Group)`
  position: relative;
`

const StyledButtonsContainer = styled.div`
  z-index: ${zIndex.reviewCartButtons};
  display: flex;
  justify-content: start;
  column-gap: ${rem(8)};
  position: relative;
`

const CondensedDeleteButton = styled(IconButton)`
  border-color: transparent;
`

interface Props {
  multiCartItems: Array<App.MultiCartInstantBookingLEHotelItem>
  banner: React.ReactNode
  showOverlay?: boolean
  hotelName: string
  headerPrice?: React.ReactNode
  checkIn: string
  checkOut: string
  duration: number
  groupDisabled?: boolean
  areAllItemsSavedForLater?: boolean
  areAllItemsDeleted?: boolean
  isAnyItemDeleted?: boolean
  fetching?: boolean
  tileVariant?: 'default' | 'condensed'
  onSaveAllForLater?: (e: React.MouseEvent<HTMLButtonElement>) => void
  onDeleteAll?: (e: React.MouseEvent<HTMLButtonElement>) => void
}

function MultiRoomHotelMultiCartReviewGroup({
  multiCartItems,
  banner,
  showOverlay,
  hotelName,
  checkIn,
  checkOut,
  duration,
  headerPrice,
  groupDisabled = false,
  areAllItemsSavedForLater = false,
  areAllItemsDeleted = false,
  isAnyItemDeleted = false,
  fetching,
  onSaveAllForLater = noop,
  onDeleteAll = noop,
  tileVariant = 'default',
}: Props) {
  const multiCartActorRef = MultiCartContext.useActorRef()
  const activeCartId = useSelector(multiCartActorRef, (snapshot) => snapshot.context.activeCartId)
  const cartItems = useSelector(multiCartActorRef, (snapshot) => snapshot.context.cartItems)
  const cartSummary = useSelector(multiCartActorRef, (snapshot) => snapshot.context.cartSummary)
  const multiCartReviewActorRef = MultiCartReviewMachineContext.useActorRef()
  const isAdmin = useAppSelector(isLEAdmin)
  const isSharedCart = cartSummary.status === 'loaded' && activeCartId && cartSummary.data[activeCartId]?.type !== 'default'

  const hotelItems = useMemo(() => {
    if (cartItems.status !== 'loaded' || !activeCartId) {
      return []
    }

    const multiCartItems = Object.values(cartItems.data[activeCartId] || {})
    return multiCartItems
      .map(multiCartItem => multiCartItem.item)
      .filter(isAccommodationItem)
  }, [cartItems, activeCartId])

  const firstMultiCartItem = multiCartItems[0]
  const [offer] = useOffer<App.HotelOffer>(firstMultiCartItem.offerId)
  const isPast = dateIsBeforeOrSame(new Date(firstMultiCartItem.checkIn), new Date())

  const summary = useMemo(() => {
    const dateRange = formatDynamicDateRange(checkIn, checkOut)
    return dateRange ? `${dateRange} · ${multiCartItems.length} rooms · ${duration} nights` : undefined
  }, [multiCartItems, checkIn, checkOut, duration])

  const offerLink = useMemo(() => {
    if (!firstMultiCartItem || !offer) return undefined

    const params = new URLSearchParams()

    const itemGroup = hotelItems.filter(isInstantBookingLEHotelItem).filter(i => i.offerId === firstMultiCartItem.offerId && i.checkIn === firstMultiCartItem.checkIn && i.checkOut === firstMultiCartItem.checkOut)
    const rooms = itemGroup.map(i => i.occupancy)

    if (!isPast) {
      params.set(queryKeyCheckIn, firstMultiCartItem.checkIn)
      params.set(queryKeyCheckOut, firstMultiCartItem.checkOut)
    }

    params.set(queryKeyAdults, rooms.map(room => room.adults).join(','))
    params.set(queryKeyChildren, rooms.map(room => encodeChildrenOccupancy(room.childrenAge)).join(','))

    return offerPageURL(offer, params)
  }, [firstMultiCartItem, hotelItems, isPast, offer])

  useEffect(() => {
    // Only save items that belong to THIS group, not all cart items
    // AND only if they're not already saved/deleted/being processed
    if (!areAllItemsSavedForLater && groupDisabled && !areAllItemsDeleted && !fetching && activeCartId) {
      const itemIdsToSave = multiCartItems
        .filter(item => item.state === 'active') // Only active items
        .map(item => item.id)

      // Early exit if no items to save
      if (itemIdsToSave.length === 0) {
        return
      }

      multiCartReviewActorRef.send({
        type: 'update save items for later',
        cartId: activeCartId,
        itemIds: itemIdsToSave,
        saveForLater: true,
        skipNotification: true,
      })
    }
  }, [activeCartId, areAllItemsDeleted, areAllItemsSavedForLater, groupDisabled, fetching, multiCartItems, multiCartReviewActorRef])

  const disableButtons = areAllItemsDeleted || isAnyItemDeleted || fetching
  const isCondensed = tileVariant === 'condensed'

  const buttons = <>
    {fetching && <LoadingMultiRoomGroupButtonsSkeleton />}
    {!fetching && (
      <StyledButtonsContainer>
        {!groupDisabled && !areAllItemsSavedForLater && (
          <TextButton kind="secondary" size="small" shape="round" disabled={disableButtons} onClick={onSaveAllForLater}>Save all for later</TextButton>
        )}
        {!groupDisabled && areAllItemsSavedForLater && (
          <TextButton kind="primary" size="small" shape="round" disabled={disableButtons} startIcon={<LinePlusIcon />} onClick={onSaveAllForLater}>Add</TextButton>
        )}
        <TextButton kind="secondary" size="small" shape="round" disabled={disableButtons} startIcon={<LineTrashAltIcon />} onClick={onDeleteAll}>Delete all</TextButton>
      </StyledButtonsContainer>
    )}
  </>

  return (
    <MultiRoomHotelMultiCartReviewGroupContainer className={cn({ 'is-condensed': isCondensed })}>
      {banner}
      <MultiRoomHotelMultiCartReviewGroupContent direction="vertical">
        <NestableClickable to={fetching ? undefined : offerLink} target="_blank">
          <Sheet background="neutral-eight" border="transparent" borderRadius="S" padding={isCondensed ? 16 : 20}>
            <Group direction="horizontal" horizontalAlign="space-between">
              {fetching && <LoadingMultiRoomGroupHeaderSkeleton />}
              {!fetching && (
                <Group direction="vertical" gap={2}>
                  <Heading variant="heading6">{hotelName}</Heading>
                  {summary && <BodyText variant="medium">{summary}</BodyText>}
                  {!groupDisabled && headerPrice}
                </Group>
              )}
              {!isCondensed && (
                <CSSBreakpoint min="tablet">
                  {buttons}
                </CSSBreakpoint>
              )}
              {isCondensed && (
                <CondensedDeleteButton kind="secondary" disabled={disableButtons} onClick={onDeleteAll}>
                  <LineTrashAltIcon />
                </CondensedDeleteButton>
              )}
            </Group>
          </Sheet>
        </NestableClickable>
        {multiCartItems.map((multiCartItem) => {
          return (
            <React.Fragment key={multiCartItem.id}>
              <Divider kind="primary" direction="horizontal" />
              {isInstantBookingLEHotelItem(multiCartItem.item) && <StyledLeHotelMultiCartReviewTile
                key={multiCartItem.id}
                multiCartItem={multiCartItem}
                hideTitleAndDates
                hideAvailabilityDetails={groupDisabled}
                disableOverlay={showOverlay} // avoid double overlay (group + individual tile)
                saveForLaterIfUnavailable={false}
                tileVariant={tileVariant}
              />}
            </React.Fragment>
          )
        })}
        {activeCartId && !isCondensed && isSharedCart && (
          <SharedCartComments
            multiCartId={activeCartId}
            itemIds={multiCartItems.map(item => item.id)}
            isAdmin={isAdmin}
            noBorder
          />
        )}
        <CSSBreakpoint max="mobile">
          <Divider kind="primary" direction="horizontal" />
          <Sheet background="neutral-eight" border="transparent" borderRadius="S" padding="16 16">
            {buttons}
          </Sheet>
        </CSSBreakpoint>
        {showOverlay && <MultiCartReviewTileOverlay />}
      </MultiRoomHotelMultiCartReviewGroupContent>
    </MultiRoomHotelMultiCartReviewGroupContainer>
  )
}

export default MultiRoomHotelMultiCartReviewGroup
